import React, { useMemo, useState } from 'react';

import Button from 'epn-ui/umd/dist/components/Button';
import Dialog from 'epn-ui/umd/dist/components/Dialog';
import Input from 'epn-ui/umd/dist/components/InputText';
import Loader from 'epn-ui/umd/dist/components/Loader';

import { toast } from 'react-toastify';

import { faCopy } from '@fortawesome/pro-regular-svg-icons/faCopy';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useI18n from '@i18n';
import copyTextToClipboard from '@utils/clipboard';

import BaseToast from '@base/BaseToast';

import apiHooks from '@apiHooks';

import { IGetCreativesDataFormatted, IPostLinkReductionDataFormatted } from '@api/types';

import { ALI_PUB, GOT_BY } from '../../config';

const { useReduceLinks } = apiHooks;

export interface IModalPartnersLinkProps {
    data: IGetCreativesDataFormatted;
    onClose: () => void;
}

export const ModalPartnerLink: React.FC<IModalPartnersLinkProps> = ({
    data: { code, offerId },
    onClose,
}) => {
    const { t } = useI18n('Creatives');
    const isAli = offerId === '1';
    const [isNeedLinkReduction, setNeedLinkReduction] = useState(false);

    const reduceLinkParams = useMemo(
        () => ({
            urlContainer: [
                { url: code, domainCutter: GOT_BY },
                { url: code, domainCutter: ALI_PUB },
            ],
        }),
        [code],
    );

    const { data: reduceLinks = [], error, isLoading } = useReduceLinks<
        IPostLinkReductionDataFormatted[]
    >({
        params: reduceLinkParams,
        condition: isNeedLinkReduction,
    });

    let aliPubLink = '';
    const checkReducedLink = reduceLinks.length;

    const findLink = reduction =>
        reduceLinks.find(({ link }) => new RegExp(reduction).test(link))?.link || '';

    if (checkReducedLink) {
        aliPubLink = findLink(ALI_PUB);
    }

    const onCopyMessage = async link => {
        await copyTextToClipboard(link);
        toast.success(<BaseToast text={t('Link copied')} type="success" />, {
            toastId: link,
        });
    };

    const closeAndClear = () => {
        setNeedLinkReduction(false);
        onClose();
    };

    const linkExists = !!checkReducedLink && !isLoading;

    const requestId = error?.response?.headers?.get('request-id');

    return (
        <Dialog title={t('Affiliate link')} onCancel={closeAndClear} open>
            <div id="modal-partner-link">
                <div className="mb-6">
                    <Input
                        value={code}
                        readOnly
                        prefix={
                            <FontAwesomeIcon icon={faCopy} onClick={() => onCopyMessage(code)} />
                        }
                        placeholder={t('Link')}
                    />
                </div>
                {!isAli && (
                    <div className="mb-6">
                        <Button onClick={() => setNeedLinkReduction(true)} type="primary">
                            {t('Shorten link')}
                        </Button>
                    </div>
                )}
                {isLoading && (
                    <div className="flex items-center justify-center">
                        <Loader />
                    </div>
                )}

                {error && (
                    <div className="p-20 text-center text-p1">
                        {t('Failed to get reduced links')}{' '}
                        {requestId && <p className="font-medium">request-id: {requestId}</p>}
                    </div>
                )}
                {linkExists && (
                    <>
                        <div className="mb-6">
                            <Input
                                value={aliPubLink}
                                readOnly
                                prefix={
                                    <FontAwesomeIcon
                                        icon={faCopy}
                                        onClick={() => onCopyMessage(aliPubLink)}
                                    />
                                }
                                placeholder={`${t('Shorten')} (${ALI_PUB})`}
                            />
                        </div>
                    </>
                )}
            </div>
        </Dialog>
    );
};
